/**
 * Chat panel
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license AGPLv3
 */

import preact from "../js/lib/preact";
import type { PSSubscription } from "./client-core";
import { PS, PSRoom, type RoomOptions, type RoomID, type Team, Config } from "./client-main";
import { PSView, PSPanelWrapper, PSRoomPanel, ReconnectTimer } from "./panels";
import { TeamForm } from "./panel-mainmenu";
import { BattleLog } from "./battle-log";
import type { Battle } from "./battle";
import { MiniEdit } from "./miniedit";
import { Dex, PSUtils, TL, toID, type ID } from "./battle-dex";
import { BattleTextParser, type Args } from "./battle-text-parser";
import { PSLoginServer } from "./client-connection";
import type { BattleRoom } from "./panel-battle";
import { BattleChoiceBuilder } from "./battle-choices";
import { ChatTournament, TournamentBox } from "./panel-chat-tournament";

declare const formatText: any; // from js/server/chat-formatter.js

type Challenge = {
	formatName: string,
	teamFormat: string,
	message?: string,
	acceptButtonLabel?: string,
	rejectButtonLabel?: string,
};

export class ChatRoom extends PSRoom {
	override readonly classType: 'chat' | 'battle' = 'chat';
	/** note: includes offline users! use onlineUsers if you need onlineUsers */
	users: { [userid: string]: string } = {};
	/** not equal to onlineUsers.length because guests exist */
	userCount = 0;
	onlineUsers: [ID, string][] = [];
	override connectMode: PSRoom['connectMode'] = 'normal';

	// PM-only properties
	pmTarget: string | null = null;
	challengeMenuOpen = false;
	initialSlash = false;
	challenging: Challenge | null = null;
	/** True after challenge send/accept before server acknowledgement */
	teamSent: string | null = null;
	challenged: Challenge | null = null;
	/** n.b. this will be null outside of battle rooms */
	battle: Battle | null = null;
	log: BattleLog | null = null;
	connectError: string | null = null;
	tour: ChatTournament | null = null;
	lastMessage: Args | null = null;
	lastViewedTime: number | null = null;

	joinLeave: { join: string[], leave: string[], messageId: string } | null = null;
	/** in order from least to most recent */
	userActivity: ID[] = [];
	timeOffset = 0;
	static highlightRegExp: Record<string, RegExp | null> | null = null;

	constructor(options: RoomOptions) {
		super(options);
		if (options.connectMode !== undefined) this.connectMode = options.connectMode;
		if (options.args?.pmTarget) this.pmTarget = options.args.pmTarget as string;
		if (options.args?.challengeMenuOpen) this.challengeMenuOpen = true;
		if (options.args?.initialSlash) this.initialSlash = true;
		this.updateTarget(this.pmTarget);
		this.connect();
	}
	override connect() {
		if (!this.connected && (
			this.connectMode === 'normal' || this.connectMode === 'pending-reconnect' ||
			this.connectMode === 'pending-login'
		)) {
			if (this.pmTarget === null) {
				PS.send(`/join ${this.id}`);
				this.connected = 'pending';
			} else {
				this.connectMode = null;
			}
		}
	}
	override interruptClose(explicit?: boolean, elem?: HTMLElement | null): string | boolean {
		if (this.type === 'chat' && this.connected === true && PS.prefs.leavePopupRoom && !explicit) {
			PS.join('confirmleaveroom' as RoomID, { parentElem: elem });
			return true;
		}
		if (this.challenging) {
			this.cancelChallenge();
		}
		return false;
	}
	override handleLine(args: Args): boolean {
		if (super.handleLine(args)) return true;
		switch (args[0]) {
		case 'users':
			const usernames = args[1].split(',');
			const count = parseInt(usernames.shift()!, 10);
			this.setUsers(count, usernames);
			return true;

		case 'join': case 'j': case 'J':
			this.addUser(args[1]);
			if (this.battle) break; // TODO: centralize in battle-log or something
			this.showJoinLeave("join", args[1], args[0] === "J");
			return true;

		case 'leave': case 'l': case 'L':
			this.removeUser(args[1]);
			if (this.battle) break; // TODO: centralize in battle-log or something
			this.showJoinLeave("leave", args[1], args[0] === "L");
			return true;

		case 'name': case 'n': case 'N':
			this.renameUser(args[1], args[2]);
			break;

		case 'tournament': case 'tournaments':
			this.tour ||= new ChatTournament(this);
			this.tour.receiveLine(args);
			return true;

		case 'noinit':
			if (this.connectMode === 'deleted') {
				if (args[2]) this.connectError = args[2];
			} else if (this.battle && args[1] === 'joinfailed') {
				this.connectError = args[2] || TL`Not found`;
			} else if (this.battle) {
				// check the Replays database
				(this as any as BattleRoom).loadReplay();
			} else {
				const title = this.title;
				this.connectError = args[2] || TL`Chatroom "${title}" not found`;
			}
			this.update(null);
			return true;
		case 'expire':
			this.connected = false;
			this.connectMode = 'deleted';
			this.connectError = args[1] || TL`This room has expired (you can't chat in it anymore)`;
			this.update(null);
			return true;

		case 'chat': case 'c':
			if (`${args[2]} `.startsWith('/challenge ')) {
				this.updateChallenge(args[1], args[2].slice(11));
				return true;
			} else if (args[2].startsWith('/warn ')) {
				const reason = args[2].replace('/warn ', '');
				PS.join(`rules-warn` as RoomID, {
					args: {
						type: 'warn',
						message: reason?.trim() || undefined,
					},
					parentElem: null,
				});
				return true;
			}
			// falls through
		case 'c:':
			if (args[0] === 'c:') PS.lastMessageTime = args[1];
			this.lastMessage = args;
			this.joinLeave = null;
			const name = args[args[0] === 'c:' ? 2 : 1];
			this.markUserActive(name);
			if (this.tour) this.tour.joinLeave = null;
			break;
		case ':':
			this.timeOffset = Math.trunc(Date.now() / 1000) - (parseInt(args[1], 10) || 0);
			PS.lastMessageTime = args[1];
			break;
		// case 'teampreview':
		// case 'turn':
		// case 'move':
		// 	this.joinLeave = null;
		// 	break;
		}
		return false;
	}
	override handleReconnect(msg: string): boolean | void {
		this.tour = null;
		this.dismissNotification('tournament-create');
		let lines = msg.split('\n');

		// cut off starting lines until we get to PS.lastMessage timestamp
		// then cut off roomintro from the end
		let cutOffStart = 0;
		let cutOffEnd = lines.length;
		const cutOffTime = PS.connection?.lastMessageTimeBeforeReconnect || 0;
		const cutOffExactLine = this.lastMessage ? '|' + this.lastMessage?.join('|') : null;
		let reconnectMessage = `|raw|<div class="infobox">${TL`You reconnected.`}</div>`;
		for (let i = 0; i < lines.length; i++) {
			if (lines[i].startsWith('|users|')) {
				this.add(lines[i]);
			}
			if (lines[i] === cutOffExactLine) {
				cutOffStart = i + 1;
			} else if (lines[i].startsWith(`|c:|`)) {
				const time = parseInt(lines[i].split('|')[2] || '');
				if (time < cutOffTime) cutOffStart = i;
			}
			if (lines[i].startsWith('|raw|<div class="infobox"> You joined ')) {
				const timestamp = BattleLog.renderTimestamp(Date.now() / 1000, PS.prefs.timestamps?.chatrooms);
				reconnectMessage = `|raw|<div class="infobox">${timestamp}${TL`You reconnected to`} ${lines[i].slice(38)}`;
				cutOffEnd = i;
				if (!lines[i - 1]) cutOffEnd = i - 1;
			}
		}
		lines = lines.slice(cutOffStart, cutOffEnd);
		if (lines[0]?.startsWith('|init|')) {
			lines[0] = `||${TL`Note: Scrollback doesn't go all the way back to when you disconnected.`}`;
		}

		if (lines.length) {
			const timestamp = BattleLog.renderTimestamp(cutOffTime, PS.prefs.timestamps?.chatrooms);
			this.receiveBatch([
				[`raw`, `<div class="infobox">${timestamp}${TL`You disconnected.`}</div>`],
				...lines.map(line => BattleTextParser.parseLine(line)),
				BattleTextParser.parseLine(reconnectMessage),
			]);
		}
		this.update(null);
		return true;
	}
	updateTarget(name?: string | null) {
		const selfWithGroup = `${PS.user.group || ' '}${PS.user.name}`;
		if (this.id === 'dm-') {
			this.pmTarget = selfWithGroup;
			this.setUsers(1, [selfWithGroup]);
			this.title = TL`Console`;
		} else if (this.id.startsWith('dm-')) {
			const id = this.id.slice(3);
			if (toID(name) !== id) name = null;
			name ||= this.pmTarget || id;
			if (/[A-Za-z0-9]/.test(name.charAt(0))) name = ` ${name}`;
			const nameWithGroup = name;
			name = name.slice(1);
			this.pmTarget = name;
			if (!PS.user.userid) {
				this.setUsers(1, [nameWithGroup]);
			} else {
				this.setUsers(2, [nameWithGroup, selfWithGroup]);
			}
			this.title = `[${TL`DM`}] ${nameWithGroup.trim()}`;
		}
	}
	static getHighlight(message: string, roomid: string) {
		let highlights = PS.prefs.highlights || {};
		if (Array.isArray(highlights)) {
			highlights = { global: highlights };
			// Migrate from the old highlight system
			PS.prefs.set('highlights', highlights);
		}
		if (!PS.prefs.noselfhighlight && PS.user.nameRegExp) {
			if (PS.user.nameRegExp?.test(message)) return true;
		}
		if (!this.highlightRegExp) {
			try {
				this.updateHighlightRegExp(highlights);
			} catch {
				// If the expression above is not a regexp, we'll get here.
				// Don't throw an exception because that would prevent the chat
				// message from showing up, or, when the lobby is initialising,
				// it will prevent the initialisation from completing.
				return false;
			}
		}
		const id = PS.server.id + '#' + roomid;
		const globalHighlightsRegExp = this.highlightRegExp?.['global'];
		const roomHighlightsRegExp = this.highlightRegExp?.[id];
		return (((globalHighlightsRegExp?.test(message)) || (roomHighlightsRegExp?.test(message))));
	}
	static updateHighlightRegExp(highlights: Record<string, string[]>) {
		// Enforce boundary for match sides, if a letter on match side is
		// a word character. For example, regular expression "a" matches
		// "a", but not "abc", while regular expression "!" matches
		// "!" and "!abc".
		this.highlightRegExp = {};
		for (let i in highlights) {
			if (!highlights[i].length) {
				this.highlightRegExp[i] = null;
				continue;
			}
			this.highlightRegExp[i] = new RegExp('(?:\\b|(?!\\w))(?:' + highlights[i].join('|') + ')(?:\\b|(?!\\w))', 'i');
		}
	}
	static isHighlightableChatMessage(message: string, isDM = false) {
		if (!message.startsWith('/')) return true;
		const [cmd] = PSUtils.splitFirst(message.slice(1), ' ');
		if (['raw', 'nonotify', 'text', 'error'].includes(cmd)) {
			return false;
		}
		if (['uhtml', 'uhtmlchange'].includes(cmd)) {
			return isDM;
		}
		if (cmd === 'subtlenotify') {
			return 'subtle';
		}
		return true;
	}
	isBeforeLastSeenMessage(serverTime: number) {
		const lastMessageDates = Dex.prefs('logtimes') || (PS.prefs.set('logtimes', {}), Dex.prefs('logtimes'));
		if (!lastMessageDates[PS.server.id]) lastMessageDates[PS.server.id] = {};
		const lastMessageDate = lastMessageDates[PS.server.id][this.id] || 0;

		// because the time offset to the server can vary slightly, subtract it to not have it affect comparisons between dates
		const time = serverTime - (this.timeOffset || 0);
		if (PS.isVisiblePanel(this)) {
			this.lastViewedTime = null;
			lastMessageDates[PS.server.id][this.id] = time;
			PS.prefs.set('logtimes', lastMessageDates);
		} else {
			// To be saved on focus
			const lastViewedTime = this.lastViewedTime || 0;
			if (lastViewedTime < time) this.lastViewedTime = time;
		}
		return time <= lastMessageDate;
	}
	getChatNotificationBody(message: string) {
		if (/^\/(log|raw|html|uhtml|uhtmlchange) /.test(message)) {
			return '"' + message.split(' ').slice(1).join(' ').replace(/<[^>]*?>/g, '') + '"';
		}
		return '"' + message + '"';
	}
	isIgnored = (name: string) => {
		if (!PS.prefs.ignore?.[toID(name)]) return false;
		// can't ignore staff
		if (!' +^\u2605\u2606'.includes(name.charAt(0))) return false;
		if (this.pmTarget !== null) return true;
		// can't ignore users in rooms you're staff in
		const roomGroup = PS.server.getGroup(this.users[PS.user.userid]);
		return roomGroup.order > PS.server.getGroup('%').order;
	};
	handleHighlight = (args: Args) => {
		let name: string;
		let message: string;
		let serverTime = 0;
		if (args[0] === 'c:') {
			serverTime = parseInt(args[1]);
			name = args[2];
			message = args[3];
		} else {
			name = args[1];
			message = args[2];
		}
		const userid = toID(name);
		if (!message) return false;
		if (userid === PS.user.userid) return false;

		const isDM = this.id.startsWith("dm-");
		const highlightType = ChatRoom.isHighlightableChatMessage(message, isDM);
		if (this.isIgnored(name) || !highlightType) return false;
		if (highlightType === 'subtle') {
			this.subtleNotify();
			return false;
		}

		if (isDM) {
			this.notify({
				title: `${this.title}`,
				body: this.getChatNotificationBody(message),
			});
			return false;
		}

		const isBeforeLastSeenMessage = (this.isBeforeLastSeenMessage(serverTime));

		if (ChatRoom.getHighlight(message, this.id)) {
			if (!isBeforeLastSeenMessage) {
				const roomid = this.id;
				this.notify({
					title: TL`Mentioned by ${name} in ${roomid}`,
					body: this.getChatNotificationBody(message),
					id: 'highlight',
				});
			}
			return true;
		}
		if (!isBeforeLastSeenMessage) this.subtleNotify();
		return false;
	};
	override clientCommands = this.parseClientCommands({
		'chall,challenge'(target) {
			if (target) {
				let [targetUser, format] = target.split(',');
				if (this.pmTarget && (format === undefined || targetUser.includes('@@@'))) {
					format = target;
					targetUser = this.pmTarget;
				}
				format = BattleLog.formatId(format || '');
				PS.mainmenu.makeQuery('userdetails', targetUser).then(data => {
					if (data.rooms === false) return this.errorReply(TL`This player does not exist or is not online.`);
					PS.join(`challenge-${toID(targetUser)}` as RoomID, { args: { format } });
				});
				return;
			}
			if (this.challengeMenuOpen) {
				this.cancelChallenge();
				return;
			}
			this.openChallenge();
		},
		'cchall,cancelchallenge'(target) {
			this.cancelChallenge();
		},
		'reject'(target) {
			this.challenged = null;
			this.teamSent = null;
			this.update(null);
			this.sendDirect(`/reject ${target}`);
		},
		'clear'() {
			this.log?.reset();
			this.update(null);
		},
		'debug'(target) {
			const targetID = toID(target);
			if (targetID === 'off') {
				PSView.setDebug(null);
				this.add('||Debug menu: OFF');
				this.handleSend('/hidedebug');
				return;
			} else if (targetID === 'snap') {
				PSView.setDebug('snap');
				this.add('||Debug menu: SCROLL-SNAP');
				return;
			} else if (targetID === 'panels') {
				PSView.setDebug('panels');
				this.add('||Debug menu: PANELS');
				return;
			} else if (targetID === 'battles') {
				this.handleSend('/showdebug');
				return;
			}
			this.add(`||Debug menu: ${PSView.debugMenu?.toUpperCase() || 'OFF'}.`);
			this.add(`||Debug battle messages: ${PS.prefs.showdebug ? 'ON' : 'OFF'}`);
			this.add('||Usage: /debug [off|snap|panels|battles]');
		},
		'togglemessages'(target) {
			if (this.pmTarget ||
				this.type !== 'chat') return this.errorReply(TL`This command can only be used in proper chat rooms.`);
			if (this.log) {
				const userid = toID(target);
				const classStart = 'revealed chat chatmessage-' + userid;
				const nodes: HTMLElement[] = [];
				let isHidden = true;
				for (const node of this.log.innerElem.childNodes as any as HTMLElement[]) {
					if (node.className && (node.className + ' ').startsWith(classStart)) {
						nodes.push(node);
					}
				}
				if (this.log.preemptElem) {
					for (const node of this.log.preemptElem.childNodes as any as HTMLElement[]) {
						if (node.className && (node.className + ' ').startsWith(classStart)) {
							nodes.push(node);
						}
					}
				}
				isHidden = nodes[0].style.display === 'none';
				nodes.every(node => {
					node.style.display = isHidden ? '' : 'none';
					return true;
				});
				isHidden = !isHidden;
				const toggleButtons = this.log.innerElem.querySelectorAll(`button[name="toggleMessages"][value="${userid}"]`);
				const hiddenCount = nodes.length;
				const buttonText = isHidden ? (hiddenCount > 1 ?
					TL`(${hiddenCount} lines from ${userid} hidden)` : TL`(${hiddenCount} line from ${userid} hidden)`
				) : (hiddenCount > 1 ?
					TL`(Hide ${hiddenCount} lines from ${userid})` : TL`(Hide ${hiddenCount} line from ${userid})`
				);
				for (const button of toggleButtons) {
					button.innerHTML = `<small>${buttonText}</small>`;
				}
			}
		},
		'rank,ranking,rating,ladder'(target) {
			if (PS.teams.usesLocalLadder) return `/rank ${target}`;

			let arg = target;
			if (!arg) {
				arg = PS.user.userid;
			}
			if (this.battle && !arg.includes(',')) {
				arg += ", " + this.id.split('-')[1];
			}

			const targets = arg.split(',');
			let formatTargeting = false;
			const formats: { [key: string]: number } = {};
			const gens: { [key: string]: number } = {};
			for (let i = 1, len = targets.length; i < len; i++) {
				targets[i] = $.trim(targets[i]);
				if (targets[i].length === 4 && targets[i].startsWith('gen')) {
					gens[targets[i]] = 1;
				} else {
					formats[toID(targets[i])] = 1;
				}
				formatTargeting = true;
			}

			PSLoginServer.query("ladderget", {
				user: targets[0],
			}).then(data => {
				if (!data || !Array.isArray(data)) return this.errorReply(TL`Error: corrupted ranking data`);
				const userLabel = TL.label(TL`User`);
				let buffer = `<div class="ladder"><table><tr><td colspan="9">${userLabel}<strong>${toID(targets[0])}</strong></td></tr>`;
				if (!data.length) {
					buffer += `<tr><td colspan="9"><em>${TL`This user has not played any ladder games yet.`}</em></td></tr>`;
					buffer += '</table></div>';
					return this.add(`|html|${buffer}`);
				}
				const eloTitle = BattleLog.escapeHTML(TL`Elo rating`);
				const gxeTitle = BattleLog.escapeHTML(TL`user's percentage chance of winning a random battle (aka GLIXARE)`);
				const glickoTitle = BattleLog.escapeHTML(TL`Glicko-1 rating: rating ± deviation`);
				buffer += `<tr><th>${TL`Format`}</th><th><abbr title="${eloTitle}">Elo</abbr></th>` +
					`<th><abbr title="${gxeTitle}">GXE</abbr></th><th><abbr title="${glickoTitle}">Glicko-1</abbr></th>` +
					`<th>COIL</th><th>${TL('W', 'wins')}</th><th>${TL('L', 'losses')}</th><th>${TL`Total`}</th>`;
				let suspect = false;
				for (const item of data) {
					if ('suspect' in item) suspect = true;
				}
				if (suspect) buffer += `<th>${TL`Suspect reqs possible?`}</th>`;
				buffer += '</tr>';
				const hiddenFormats = [];
				for (const row of data) {
					if (!row) return this.errorReply(TL`Error: corrupted ranking data`);
					const formatId = toID(row.formatid);
					const matchesTarget = (
						formats[formatId] ||
						gens[formatId.slice(0, 4)] ||
						(gens['gen6'] && !formatId.startsWith('gen'))
					);
					if (matchesTarget || (!formatTargeting && row.elo >= 1001 && (row.w + row.l + row.t > 0))) {
						buffer += '<tr>';
					} else {
						buffer += '<tr class="hidden">';
						hiddenFormats.push(window.BattleLog.escapeFormat(formatId, true));
					}

					// Validate all the numerical data
					for (const value of [row.elo, row.rpr, row.rprd, row.gxe, row.w, row.l, row.t]) {
						if (typeof value !== 'number' && typeof value !== 'string') {
							return this.errorReply(TL`Error: corrupted ranking data`);
						}
					}

					buffer += `<td> ${BattleLog.escapeHTML(BattleLog.formatName(formatId, true))} </td><td><strong>${Math.round(row.elo)}</strong></td>`;
					if (row.rprd > 100) {
						// High rating deviation. Provisional rating.
						buffer += `<td>&ndash;</td>`;
						buffer += `<td><span style="color:#888"><em>${Math.round(row.rpr)} <small> &#177; ${Math.round(row.rprd)} </small></em> <small>${TL`(provisional)`}</small></span></td>`;
					} else {
						buffer += `<td>${Math.trunc(row.gxe)}<small>.${row.gxe.toFixed(1).slice(-1)}%</small></td>`;
						buffer += `<td><em>${Math.round(row.rpr)} <small> &#177; ${Math.round(row.rprd)}</small></em></td>`;
					}
					const N = parseInt(row.w, 10) + parseInt(row.l, 10) + parseInt(row.t, 10);

					if (row.coil) {
						buffer += `<td>${Math.round(row.coil)}</td>`;
					} else {
						buffer += '<td>&mdash;</td>';
					}
					buffer += `<td> ${row.w} </td><td> ${row.l} </td><td> ${N} </td>`;
					if (suspect) {
						if (typeof row.suspect === 'undefined') {
							buffer += '<td>&mdash;</td>';
						} else {
							buffer += '<td>';
							buffer += (row.suspect ? TL`Yes` : TL`No`);
							buffer += '</td>';
						}
					}
					buffer += '</tr>';
				}
				if (hiddenFormats.length) {
					if (hiddenFormats.length === data.length) {
						if (formatTargeting) {
							const formatsText = BattleLog.escapeHTML(Object.keys(gens).concat(Object.keys(formats)).join(', '));
							buffer += `<tr class="no-matches"><td colspan="8">` +
								`<em>${TL`This user has not played any ladder games that match ${formatsText}.`}</em></td></tr>`;
						} else {
							buffer += `<tr class="no-matches"><td colspan="8"><em>${TL`This user has no notable ladder activity.`}</em></td></tr>`;
						}
					}
					const hiddenCount = hiddenFormats.length;
					const showHiddenText = hiddenCount === 1 ?
						TL`Show ${hiddenCount} hidden format` : TL`Show ${hiddenCount} hidden formats`;
					buffer += `<tr><td colspan="8"><button class="button" name="showOtherFormats">` +
						`${showHiddenText}</button></td></tr>`;
				}
				let userid = toID(targets[0]);
				let registered = PS.user.registered;
				if (registered && PS.user.userid === userid) {
					buffer += `<tr><td colspan="8" style="text-align:right"><a href="//${Config.routes.users}/${userid}">${TL`Reset W/L`}</a></tr></td>`;
				}
				buffer += '</table></div>';
				this.add(`|html|${buffer}`);
			});
		},

		// battle-specific commands
		// ------------------------
		'play'() {
			if (!this.battle) return this.errorReply(TL`You are not in a battle`);
			if (this.battle.atQueueEnd) {
				if (this.battle.ended) this.battle.isReplay = true;
				this.battle.reset();
			}
			this.battle.play();
			this.update(null);
		},
		'pause'() {
			if (!this.battle) return this.errorReply(TL`You are not in a battle`);
			this.battle.pause();
			this.update(null);
		},
		'ffto,fastfowardto'(target, cmd, parentElem) {
			if (!this.battle) return this.errorReply(TL`You are not in a battle`);
			if (!target) {
				PS.prompt(TL`Turn number?`, {
					defaultValue: `${this.battle.turn}`,
					type: 'numeric',
					okButton: TL`[Go]`,
					parentElem,
				}).then(turnNum => {
					if (turnNum?.trim()) this.send(`/ffto ${turnNum}`, parentElem);
				});
				return;
			}

			let turnNum = Number(target);
			if (target.startsWith('+') || turnNum < 0) {
				turnNum += this.battle.seeking ?? this.battle.turn;
				if (turnNum < 0) turnNum = 0;
			} else if (target === 'end') {
				turnNum = Infinity;
			}
			if (isNaN(turnNum)) {
				this.errorReply(TL`Invalid turn number: ${target}`);
				return;
			}
			if (this.battle.hardcoreMode) {
				this.errorReply(TL`Turn navigation is disabled in hardcore mode.`);
				return;
			}
			this.battle.seekTurn(turnNum);
			this.update(null);
		},
		'switchsides'() {
			if (!this.battle) return this.errorReply(TL`You are not in a battle`);
			this.battle.switchViewpoint();
		},
		'cancel,undo,cancelone'(_target, cmd) {
			if (!this.battle) return this.send('/cancelchallenge');

			const room = this as any as BattleRoom;
			if (!room.choices || !room.request) {
				this.errorReply('/choose - ' + TL`You are not a player in this battle`);
				return;
			}
			if (room.choices.isDone() || room.choices.isEmpty()) {
				// we _could_ check choices.noCancel, but the server will check anyway
				this.sendDirect('/undo');
			}
			if (cmd === 'cancel') {
				room.choices = new BattleChoiceBuilder(room.request);
			} else {
				room.choices = room.choices.previous();
			}
			room.updateChoiceNotification();
			this.update(null);
		},
		'move,switch,team,pass,shift,choose'(target, cmd) {
			if (!this.battle) return this.errorReply(TL`You are not in a battle`);
			const room = this as any as BattleRoom;
			if (!room.choices || !room.request) {
				this.errorReply('/choose - ' + TL`You are not a player in this battle`);
				return;
			}

			const choices = cmd === 'choose' ? new BattleChoiceBuilder(room.request) : room.choices;
			if (cmd !== 'choose') target = `${cmd} ${target}`;
			const possibleError = choices.addChoices(target);
			if (possibleError) {
				this.errorReply(possibleError);
				return;
			}

			room.choices = choices;
			if (choices.isDone()) {
				this.sendDirect(`/choose ${choices.toString()}`);
			}
			room.updateChoiceNotification();
			room.overlayActive = null;
			this.update(null);
		},
		'movemenu,switchmenu'(target, cmd) {
			if (!this.battle) return this.errorReply(TL`You are not in a battle`);
			const room = this as any as BattleRoom;
			if (!target && cmd === 'movemenu') {
				room.overlayActive = (room.overlayActive === 'move' ? null : 'move');
				this.update(null);
				return;
			}
			if (!target && cmd === 'switchmenu') {
				room.overlayActive = (room.overlayActive === 'switch' ? null : 'switch');
				this.update(null);
				return;
			}
			this.errorReply('???');
		},
	});
	openChallenge() {
		if (!this.pmTarget) {
			this.errorReply(TL`Can only be used in a DM.`);
			return;
		}
		this.challengeMenuOpen = true;
		this.update(null);
	}
	cancelChallenge() {
		if (!this.pmTarget) {
			this.errorReply(TL`Can only be used in a DM.`);
			return;
		}
		if ((this.teamSent && this.challengeMenuOpen) || this.challenging) {
			this.sendDirect('/cancelchallenge');
			this.challenging = null;
			this.challengeMenuOpen = true;
		} else {
			this.challengeMenuOpen = false;
		}
		this.challenging = null;
		this.teamSent = null;
		this.update(null);
	}
	parseChallenge(challengeString: string | null): Challenge | null {
		if (!challengeString) return null;

		let splitChallenge = challengeString.split('|');

		const challenge = {
			formatName: splitChallenge[0],
			teamFormat: splitChallenge[1] ?? splitChallenge[0],
			message: splitChallenge[2],
			acceptButtonLabel: splitChallenge[3],
			rejectButtonLabel: splitChallenge[4],
		};
		if (!challenge.formatName && !challenge.message) {
			return null;
		}
		return challenge;
	}
	updateChallenge(name: string, challengeString: string) {
		const challenge = this.parseChallenge(challengeString);
		const userid = toID(name);
		if (this.args?.format) this.args.format = null;
		this.teamSent = null;

		// Protocol documentation: https://github.com/smogon/pokemon-showdown-client/pull/1799

		if (!challenge) {
			// rejected or canceled.
			// plausibly due to a server bug, SENDER may be wrong in this case
			// (when we reject, we are SENDER; when we cancel, we are not)
			this.challenged = null;
			this.challenging = null;
		} else if (userid === PS.user.userid) {
			// we are SENDER
			this.challenging = challenge;
			this.challengeMenuOpen = false;
			PS.mainmenu.lastChallenged = Date.now();
		} else {
			// we are RECEIVER
			this.challenged = challenge;
			this.notify({
				title: TL`Challenge from ${name}`,
				body: TL.label(TL`Format`, BattleLog.formatName(challenge.formatName)),
				id: 'challenge',
			});
		}
		this.update(null);
	}
	markUserActive(name: string) {
		const userid = toID(name);
		const idx = this.userActivity.indexOf(userid);
		this.users[userid] = name;
		if (idx !== -1) {
			this.userActivity.splice(idx, 1);
		}
		this.userActivity.push(userid);
		if (this.userActivity.length > 100) {
			// Prune the list
			this.userActivity.splice(0, 20);
		}
	}
	override sendDirect(line: string) {
		if (this.pmTarget) {
			line = line.split('\n').filter(Boolean).map(row => `/pm ${this.pmTarget!}, ${row}`).join('\n');
			PS.send(line);
			return;
		}
		super.sendDirect(line);
	}
	setUsers(count: number, usernames: string[]) {
		this.userCount = count;
		this.onlineUsers = [];
		for (const username of usernames) {
			const userid = toID(username);
			this.users[userid] = username;
			this.onlineUsers.push([userid, username]);
		}
		this.sortOnlineUsers();
		this.update(null);
	}
	sortOnlineUsers() {
		PSUtils.sortBy(this.onlineUsers, ([id, name]) => (
			[PS.server.getGroup(name.charAt(0)).order, !name.endsWith('@!'), id]
		));
	}
	addUser(username: string) {
		if (!username) return;

		const userid = toID(username);
		this.users[userid] = username;
		const index = this.onlineUsers.findIndex(([curUserid]) => curUserid === userid);
		if (index >= 0) {
			this.onlineUsers[index] = [userid, username];
		} else {
			this.userCount++;
			this.onlineUsers.push([userid, username]);
			this.sortOnlineUsers();
		}
		this.update(null);
	}
	removeUser(username: string, noUpdate?: boolean) {
		if (!username) return;

		const userid = toID(username);
		const index = this.onlineUsers.findIndex(([curUserid]) => curUserid === userid);
		if (index >= 0) {
			this.userCount--;
			this.onlineUsers.splice(index, 1);
			if (!noUpdate) this.update(null);
		}
	}
	renameUser(username: string, oldUsername: string) {
		this.removeUser(oldUsername, true);
		this.addUser(username);
		this.update(null);
	}

	showJoinLeave(action: 'join' | 'leave', name: string, silent: boolean) {
		const showjoins = PS.prefs.showjoins?.[PS.server.id];
		if (!(showjoins?.[this.id] ?? showjoins?.['global'] ?? !silent)) return;

		this.joinLeave ||= {
			join: [],
			leave: [],
			messageId: `joinleave-${Date.now()}`,
		};
		const user = BattleTextParser.parseNameParts(name);
		const formattedName = user.group + user.name;
		if (action === 'join' && this.joinLeave['leave'].includes(formattedName)) {
			this.joinLeave['leave'].splice(this.joinLeave['leave'].indexOf(formattedName), 1);
		} else if (action === 'leave' && this.joinLeave['join'].includes(formattedName)) {
			this.joinLeave['join'].splice(this.joinLeave['join'].indexOf(formattedName), 1);
		} else {
			this.joinLeave[action].push(formattedName);
		}

		const joinedMessage = this.formatJoinLeave(this.joinLeave['join'], 'joined');
		const leftMessage = this.formatJoinLeave(this.joinLeave['leave'], 'left');
		const message = joinedMessage && leftMessage ? TL`${joinedMessage}; ${leftMessage}` : joinedMessage || leftMessage;

		this.add(`|uhtml|${this.joinLeave.messageId}|<small class="gray">${message}</small>`);
	}

	formatJoinLeave(preList: string[], action: 'joined' | 'left') {
		if (!preList.length) return '';

		// CobblemonShowdown : messages traduits (ils étaient écrits en anglais en dur).
		const list = preList.filter((name, i) => preList.indexOf(name) === i).map(name => BattleLog.escapeHTML(name));
		let message = list[0];
		if (list.length > 5) {
			message = TL`${list.slice(0, 5).join(', ')} and ${list.length - 5} others`;
		} else if (list.length > 1) {
			message = TL`${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
		}
		return action === 'joined' ? TL`${message} joined` : TL`${message} left`;
	}

	override destroy() {
		if (this.battle) {
			// since battle is defined here, we might as well deallocate it here
			this.battle.destroy();
		} else {
			this.log?.destroy();
		}
		super.destroy();
	}
}

export class CopyableURLBox extends preact.Component<{ url: string }> {
	copy = () => {
		const input = this.base!.children[0] as HTMLInputElement;
		input.select();
		document.execCommand('copy');
	};
	override render() {
		return <div>
			<input
				name="url" type="text" class="textbox" readOnly size={45} value={this.props.url}
				style="field-sizing:content"
			/> {}
			<button class="button" onClick={this.copy}>{TL`[Copy]`}</button> {}
			<a href={this.props.url} target="_blank" class="no-panel-intercept">
				<button class="button">{TL`[Visit]`}</button>
			</a>
		</div>;
	}
}

interface UserAutoCompleteCandidate {
	type: "user";
	userid: string;
	prefixIndex: number;
}

interface CmdAutoCompleteCandidate {
	type: "command";
	command: string;
}

export type AutoCompleteCandidate = UserAutoCompleteCandidate | CmdAutoCompleteCandidate;

export class ChatTextEntry extends preact.Component<{
	room: ChatRoom, onMessage: (msg: string, elem: HTMLElement) => void, onKey: (e: KeyboardEvent) => boolean,
	left?: number, tinyLayout?: boolean,
}> {
	subscription: PSSubscription | null = null;
	textbox: HTMLTextAreaElement | null = null;
	miniedit: MiniEdit | null = null;
	history: string[] = [];
	historyIndex = 0;
	tabComplete: {
		candidates: AutoCompleteCandidate[],
		candidateIndex: number,
		/** the text left of the cursor before tab completing */
		prefix: string,
		/** the text left of the cursor after tab completing */
		cursor: string,
	} | null = null;
	override componentDidMount() {
		this.subscription = PS.user.subscribe(() => {
			this.forceUpdate();
		});
		const textbox = this.base!.querySelector<HTMLElement>('textarea[name=message], pre, input[name=url]')!;
		if (textbox.tagName === 'TEXTAREA' || textbox.tagName === 'INPUT') {
			// hack to support replays with no textbox. hopefully doesn't crash
			this.textbox = textbox as HTMLTextAreaElement;
		} else {
			this.miniedit = new MiniEdit(textbox, {
				setContent: text => {
					textbox.innerHTML = formatText(text, false, false, true) + '\n';
					textbox.classList?.toggle('textbox-empty', !text);
				},
				onKeyDown: this.onKeyDown,
			});
		}
		if (this.props.room.args?.initialSlash) {
			this.props.room.args.initialSlash = false;
			this.setValue('/', 1);
		}
		(this.props.room as any).__textentry = this;
	}
	override componentWillUnmount() {
		if (this.subscription) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
		this.miniedit?.destroy();
	}
	focusIfNoSelection = (e: Event) => {
		if ((e.target as HTMLElement).tagName === 'TEXTAREA') return;
		const selection = window.getSelection()!;
		if (selection.type === 'Range') return;
		const elem = this.base!.children[0].children[1] as HTMLTextAreaElement;
		PSView.politeFocus(elem);
	};
	submit() {
		this.props.onMessage(this.getValue(), this.miniedit?.element || this.textbox!);
		this.historyPush(this.getValue());
		this.setValue('', 0);
		return true;
	}
	onKeyDown = (e: KeyboardEvent) => {
		if (e.isComposing) return;
		if (this.handleKey(e) || this.props.onKey(e)) {
			e.preventDefault();
			e.stopImmediatePropagation();
		}
	};

	// Direct manipulation functions
	getValue() {
		return this.miniedit ? this.miniedit.getValue() : this.textbox!.value;
	}
	setValue(value: string, start: number, end = start) {
		if (this.miniedit) {
			this.miniedit.setValue(value, { start, end });
		} else {
			this.textbox!.value = value;
			this.textbox!.setSelectionRange?.(start, end);
		}
	}
	getSelection() {
		const value = this.getValue();
		let { start, end } = this.miniedit ?
			(this.miniedit.getSelection() || { start: value.length, end: value.length }) :
			{ start: this.textbox!.selectionStart, end: this.textbox!.selectionEnd };
		return { value, start, end };
	}
	setSelection(start: number, end: number) {
		if (this.miniedit) {
			this.miniedit.setSelection({ start, end });
		} else {
			this.textbox!.setSelectionRange?.(start, end);
		}
	}
	replaceSelection(text: string) {
		if (this.miniedit) {
			this.miniedit.replaceSelection(text);
		} else {
			const { value, start, end } = this.getSelection();
			const newSelection = start + text.length;
			this.setValue(value.slice(0, start) + text + value.slice(end), newSelection);
		}
	}

	historyUp(ifSelectionCorrect?: boolean) {
		if (ifSelectionCorrect) {
			const { value, start, end } = this.getSelection();
			if (start !== end) return false; // never traverse history if text is selected
			if (end !== 0) {
				if (end < value.length) return false; // only go up at start or end of line
			}
		}

		if (this.historyIndex === 0) return false;
		const line = this.getValue();
		if (line !== '') this.history[this.historyIndex] = line;
		const newValue = this.history[--this.historyIndex];
		this.setValue(newValue, newValue.length);
		return true;
	}
	historyDown(ifSelectionCorrect?: boolean) {
		if (ifSelectionCorrect) {
			const { value, start, end } = this.getSelection();
			if (start !== end) return false; // never traverse history if text is selected
			if (end < value.length) return false; // only go down at end of line
		}

		const line = this.getValue();
		if (line !== '') this.history[this.historyIndex] = line;
		if (this.historyIndex === this.history.length) {
			if (!line) return false;
			this.setValue('', 0);
		} else if (++this.historyIndex === this.history.length) {
			this.setValue('', 0);
		} else {
			const newValue = this.history[this.historyIndex];
			this.setValue(newValue, newValue.length);
		}
		return true;
	}
	historyPush(line: string) {
		const duplicateIndex = this.history.lastIndexOf(line);
		if (duplicateIndex >= 0) this.history.splice(duplicateIndex, 1);
		if (this.history.length > 100) this.history.splice(0, 20);
		this.history.push(line);
		this.historyIndex = this.history.length;
	}
	handleKey(ev: KeyboardEvent) {
		const cmdKey = ((ev.metaKey ? 1 : 0) + (ev.ctrlKey ? 1 : 0) === 1) && !ev.altKey && !ev.shiftKey;
		// const anyModifier = ev.ctrlKey || ev.altKey || ev.metaKey || ev.shiftKey;
		if (ev.keyCode === 13 && !ev.shiftKey) { // Enter key
			return this.submit();
		} else if (ev.keyCode === 13) { // enter
			this.replaceSelection('\n');
			return true;
		} else if (ev.keyCode === 73 && cmdKey) { // Ctrl + I key
			return this.toggleFormatChar('_');
		} else if (ev.keyCode === 66 && cmdKey) { // Ctrl + B key
			return this.toggleFormatChar('*');
		} else if (ev.keyCode === 192 && cmdKey) { // Ctrl + ` key
			return this.toggleFormatChar('`');
		} else if (ev.keyCode === 9 && !ev.ctrlKey) { // Tab key
			const reverse = !!ev.shiftKey; // Shift+Tab reverses direction
			return this.handleTabComplete(reverse);
		} else if (ev.keyCode === 38 && !ev.shiftKey && !ev.altKey) { // Up key
			return this.historyUp(true);
		} else if (ev.keyCode === 40 && !ev.shiftKey && !ev.altKey) { // Down key
			return this.historyDown(true);
		} else if (ev.keyCode === 27) { // esc
			if (this.undoTabComplete()) {
				return true;
			}
			if (PS.room !== PS.getPanel()) { // only close if in mini-room mode
				PS.leave(PS.room.id);
				return true;
			}
		// } else if (e.keyCode === 32 && PS.user.lastPM && ['/reply', '/r', '/R'].includes(this.getValue())) { // '/reply ' is being written
		// 	const newValue = `/pm ${PS.user.lastPM}, `;
		// 	this.setValue(newValue, newValue.length);
		// 	return true;
		}
		return false;
	}
	// TODO - add support for commands tabcomplete
	handleTabComplete(reverse: boolean): boolean {
		// Don't tab complete at the start of the text box.
		let { value, start, end } = this.getSelection();
		if (start !== end || end === 0) return false;

		const users = this.props.room.users;
		let prefix = value.slice(0, end);
		if (prefix === this.tabComplete?.cursor) {
			// The user is cycling through the candidate names.
			if (reverse) {
				this.tabComplete.candidateIndex--;
				if (this.tabComplete.candidateIndex < 0) {
					this.tabComplete.candidateIndex = this.tabComplete.candidates.length - 1;
				}
			} else {
				this.tabComplete.candidateIndex++;
				if (this.tabComplete.candidateIndex >= this.tabComplete.candidates.length) {
					this.tabComplete.candidateIndex = 0;
				}
			}
		} else if (!value || reverse) {
			// not tab completing - let them focus things
			return false;
		} else {
			// This is a new tab completion.
			// There needs to be non-whitespace to the left of the cursor.
			// no command prefixes either, we're testing for usernames here.
			prefix = prefix.trim();

			/** match of the closest word left of the cursor */
			const match1 = /^([\s\S!/]*?)([A-Za-z0-9][^, \n]*)$/.exec(prefix);
			/** match of the closest two words left of the cursor */
			const match2 = /^([\s\S!/]*?)([A-Za-z0-9][^, \n]* [^, ]*)$/.exec(prefix);
			if (!match1 && !match2) return true;

			const candidates: AutoCompleteCandidate[] = [];
			const idprefix = (match1 ? toID(match1[2]) : '');
			let spaceprefix = (match2 ? match2[2].replace(/[^A-Za-z0-9 ]+/g, '').toLowerCase() : '');
			if (match2 && (match2[0] === '/' || match2[0] === '!')) spaceprefix = '';
			for (const userid in users) {
				if (spaceprefix && users[userid].slice(1).replace(/[^A-Za-z0-9 ]+/g, '')
					.toLowerCase()
					.startsWith(spaceprefix)) {
					if (match2) candidates.push({ type: "user", userid, prefixIndex: match2[1].length });
				} else if (idprefix && userid.startsWith(idprefix)) {
					if (match1) candidates.push({ type: "user", userid, prefixIndex: match1[1].length });
				}
			}
			// Sort by most recent to speak in the chat, or, in the case of a tie,
			// in alphabetical order.
			const userActivity = this.props.room.userActivity;
			candidates.sort((a, b) => {
				// command autocomplete options aren't added until after the user autocomplete options are sorted.
				if (a.type !== "user" || b.type !== "user") return 0;
				if (a.prefixIndex !== b.prefixIndex) {
					// shorter prefix length comes first
					return a.prefixIndex - b.prefixIndex;
				}
				const aIndex = userActivity?.indexOf(a.userid as ID) ?? -1;
				const bIndex = userActivity?.indexOf(b.userid as ID) ?? -1;
				if (aIndex !== bIndex) {
					return bIndex - aIndex; // -1 is fortunately already in the correct order
				}
				return (a.userid < b.userid) ? -1 : 1; // alphabetical order
			});

			const currentLine = prefix.substring(prefix.lastIndexOf('\n') + 1);
			const isCommandWord = (word: string) => (word.startsWith('/') && !word.startsWith('//')) || word.startsWith('!');
			const currentWord = currentLine.substring(currentLine.lastIndexOf(' ') + 1);
			const isCommandSearch = isCommandWord(currentWord);
			if (isCommandSearch) {
				PS.mainmenu.makeQuery('cmdsearch', currentWord, true).then((data: string[]) => {
					const cmds = data.sort((a, b) => a.length < b.length ? 1 : -1);
					const nextCmd = cmds[cmds.length - 1];
					const newValue = nextCmd + value.substring(end);
					this.setValue(newValue, nextCmd.length, nextCmd.length);
					const currentCandidates = this.tabComplete?.candidates ?? [];
					for (const cmd of cmds) {
						currentCandidates.unshift({ type: "command", command: cmd });
					}
					this.tabComplete = {
						candidates: currentCandidates,
						candidateIndex: 0,
						prefix: nextCmd,
						cursor: nextCmd,
					};
				});
				return true;
			}

			if (!candidates.length) {
				this.tabComplete = null;
				return true;
			}
			this.tabComplete = {
				candidates,
				candidateIndex: 0,
				prefix,
				cursor: prefix,
			};
		}
		// Substitute in the tab-completed name
		const candidate = this.tabComplete.candidates[this.tabComplete.candidateIndex];
		if (candidate.type === "user") {
			let name = users[candidate.userid];
			if (!name) return true;

			name = Dex.getShortName(name.slice(1)); // Remove rank and busy characters
			const cursor = this.tabComplete.prefix.slice(0, candidate.prefixIndex) + name;
			this.setValue(cursor + value.slice(end), cursor.length);
			this.tabComplete.cursor = cursor;
		} else {
			const prefixIndex = prefix.lastIndexOf('\n') + 1;
			const fullPrefix = prefix.substring(0, prefixIndex) + Dex.getShortName(candidate.command);
			const newValue = fullPrefix + value.substring(end);
			this.setValue(newValue, fullPrefix.length, fullPrefix.length);
			this.tabComplete.cursor = fullPrefix;
			this.tabComplete.prefix = fullPrefix;
		}
		return true;
	}
	undoTabComplete() {
		if (!this.tabComplete) return false;
		const value = this.getValue();
		if (!value.startsWith(this.tabComplete.cursor)) return false;

		this.setValue(this.tabComplete.prefix + value.slice(this.tabComplete.cursor.length), this.tabComplete.prefix.length);
		this.tabComplete = null;
		return true;
	}
	toggleFormatChar(formatChar: string) {
		let { value, start, end } = this.getSelection();

		// make sure start and end aren't midway through the syntax
		if (value.charAt(start) === formatChar && value.charAt(start - 1) === formatChar &&
			value.charAt(start - 2) !== formatChar) {
			start++;
		}
		if (value.charAt(end) === formatChar && value.charAt(end - 1) === formatChar &&
			value.charAt(end - 2) !== formatChar) {
			end--;
		}

		// wrap in doubled format char
		const wrap = formatChar + formatChar;
		value = value.slice(0, start) + wrap + value.slice(start, end) + wrap + value.slice(end);
		start += 2;
		end += 2;

		// prevent nesting
		const nesting = wrap + wrap;
		if (value.slice(start - 4, start) === nesting) {
			value = value.slice(0, start - 4) + value.slice(start);
			start -= 4;
			end -= 4;
		} else if (start !== end && value.slice(start - 2, start + 2) === nesting) {
			value = value.slice(0, start - 2) + value.slice(start + 2);
			start -= 2;
			end -= 4;
		}
		if (value.slice(end, end + 4) === nesting) {
			value = value.slice(0, end) + value.slice(end + 4);
		} else if (start !== end && value.slice(end - 2, end + 2) === nesting) {
			value = value.slice(0, end - 2) + value.slice(end + 2);
			end -= 2;
		}

		this.setValue(value, start, end);
		return true;
	}
	override render() {
		const { room } = this.props;
		const OLD_TEXTBOX = !PSView.useContentEditable && !this.miniedit;
		if (room.connectMode === null && room.id.startsWith('battle-')) {
			return <div
				class="chat-log-add hasuserlist" onClick={this.focusIfNoSelection} style={{ left: this.props.left || 0 }}
			><CopyableURLBox url={`${location.protocol}//${location.host}/${room.id}`} /></div>;
		}

		const canTalk = PS.user.named || room.id === 'dm-';
		const connected = room.connected === true || room.connectMode === null;
		return <div
			class="chat-log-add" onClick={this.focusIfNoSelection} style={{ left: this.props.left || 0 }}
		>
			<form class={`chatbox${this.props.tinyLayout ? ' nolabel' : ''}`} style={canTalk ? {} : { display: 'none' }}>
				<label style={`color:${BattleLog.usernameColor(PS.user.userid)}`}>{PS.user.name}:</label>
				{OLD_TEXTBOX ? <PSTextarea
					name="message"
					class={connected && canTalk ? 'autofocus' : 'disabled'} minHeight="16px"
					placeholder={PSView.focusPreview(room)} onKeyDown={this.onKeyDown}
				/> : <ChatTextBox
					disabled={!connected || !canTalk}
					placeholder={PSView.focusPreview(room)}
				/>}
			</form>
			{!canTalk && <button data-href="login" class="button autofocus">
				{TL`[Choose a name before sending messages]`}
			</button>}
		</div>;
	}
}

class ChatTextBox extends preact.Component<{ placeholder: string, disabled?: boolean }> {
	override shouldComponentUpdate(nextProps: any) {
		this.base!.setAttribute("placeholder", nextProps.placeholder);
		this.base!.classList?.toggle('disabled', !!nextProps.disabled);
		this.base!.classList?.toggle('autofocus', !nextProps.disabled);
		return false;
	}
	override render() {
		return <pre
			class={`textbox textbox-empty ${this.props.disabled ? ' disabled' : ' autofocus'}`} placeholder={this.props.placeholder}
		>{'\n'}</pre>;
	}
}

class ChatPanel extends PSRoomPanel<ChatRoom> {
	static readonly id = 'chat';
	static readonly routes = ['dm-*', 'groupchat-*', '*'];
	static readonly Model = ChatRoom;
	static readonly location = 'right';
	static readonly icon = <i class="fa fa-comment-o" aria-hidden></i>;
	override componentDidMount(): void {
		super.componentDidMount();
		this.subscribeTo(PS.user, () => {
			this.props.room.updateTarget();
		});
	}
	send = (text: string, elem: HTMLElement) => {
		this.props.room.send(text, elem);
	};
	onKey = (e: KeyboardEvent) => {
		if (e.keyCode === 33) { // Pg Up key
			const chatLog = this.base!.getElementsByClassName('chat-log')[0] as HTMLDivElement;
			chatLog.scrollTop = chatLog.scrollTop - chatLog.offsetHeight + 60;
			return true;
		} else if (e.keyCode === 34) { // Pg Dn key
			const chatLog = this.base!.getElementsByClassName('chat-log')[0] as HTMLDivElement;
			chatLog.scrollTop = chatLog.scrollTop + chatLog.offsetHeight - 60;
			return true;
		}
		return false;
	};
	makeChallenge = (e: Event, format: string, team?: Team) => {
		const elem = e.target as HTMLElement;
		const now = Date.now();
		const lastChallenged = PS.mainmenu.lastChallenged || 0;
		if (now - lastChallenged < 5_000) {
			PS.alert(TL`Please wait 5 seconds before challenging again.`, {
				parentElem: elem,
			});
			return;
		}

		PS.requestNotifications();
		const room = this.props.room;
		const packedTeam = team ? team.packedTeam : '';
		const privacy = PS.mainmenu.adjustPrivacy();
		if (!room.pmTarget) throw new Error("Not a PM room");
		PS.send(`/utm ${packedTeam}`);
		PS.send(`${privacy}/challenge ${room.pmTarget}, ${format}`);
		room.teamSent = format || '-';
		room.update(null);
	};
	acceptChallenge = (e: Event, format: string, team?: Team) => {
		const room = this.props.room;
		const packedTeam = team ? team.packedTeam : '';
		if (!room.pmTarget) throw new Error("Not a PM room");
		PS.send(`/utm ${packedTeam}`);
		this.props.room.send(`/accept`);
		room.teamSent = format || '-';
		room.update(null);
	};
	renderControls() {
		const room = this.props.room;
		const connectError = this.renderConnectError();

		const defaultFormat = room.args?.format as string | undefined;
		if (defaultFormat?.startsWith('!!')) {
			room.args!.format = undefined;
		}

		const challengeSent = room.teamSent && !room.challenged;
		const challengeTo = room.challenging ? <div class="challenge outgoing">
			<p>{TL`Waiting for ${room.pmTarget}...`}</p>
			<TeamForm
				format={room.challenging.formatName} teamFormat={room.challenging.teamFormat}
				onSubmit={null} selectType="challenge"
			>
				<button data-cmd="/cancelchallenge" class="button">{TL`[Cancel]`}</button>
			</TeamForm>
		</div> : room.challengeMenuOpen ? <div class="challenge outgoing">
			<TeamForm onSubmit={this.makeChallenge} defaultFormat={defaultFormat} selectType="challenge">
				{challengeSent && <button class="button" disabled>
					{TL`Challenging...`}
				</button>}
				{!challengeSent && <button type="submit" class="button button-first" disabled={!!room.challenged}>
					<strong>{TL`[Challenge]`}</strong>
				</button>}
				{!challengeSent && <button data-href="battleoptions" class="button button-last" aria-label={TL`[Battle options]`}>
					<i class="fa fa-caret-down" aria-hidden></i>
				</button>} {}
				<button data-cmd="/cancelchallenge" class="button">{TL`[Cancel]`}</button>
			</TeamForm>
		</div> : null;

		const challengeFrom = room.challenged ? <div class="challenge">
			{!!room.challenged.message && <p>{room.challenged.message}</p>}
			<TeamForm
				format={room.challenged.formatName} teamFormat={room.challenged.teamFormat}
				onSubmit={this.acceptChallenge} selectType="challenge"
			>
				{room.teamSent && <button class="button" disabled>
					{TL`Accepting...`}
				</button>}
				{!room.teamSent && <button
					type="submit" class={room.challenged.formatName ? `button button-first` : `button`}
					data-cmdpreview="/accept"
				>
					<strong>{room.challenged.acceptButtonLabel || TL`[Accept]`}</strong>
				</button>}
				{!room.teamSent && room.challenged.formatName && <button
					data-href="battleoptions" class="button button-last" aria-label={TL`[Battle options]`}
				>
					<i class="fa fa-caret-down" aria-hidden></i>
				</button>} {}
				<button data-cmd="/reject" class="button">{room.challenged.rejectButtonLabel || TL`[Reject]`}</button>
			</TeamForm>
		</div> : null;

		if (!challengeTo && !challengeFrom && !PS.isOffline && !connectError) return null;
		return <>
			{connectError}
			{challengeTo}{challengeFrom}{PS.isOffline && <p class="buttonbar">
				<button class="button" data-cmd="/reconnect">
					<i class="fa fa-plug" aria-hidden></i> <strong>{TL`[Reconnect]`}</strong>
				</button> {}
				<ReconnectTimer />
			</p>}
		</>;
	}
	renderConnectError() {
		const room = this.props.room;
		if (room.connectMode !== 'deleted' && room.connectMode !== 'not-found') {
			return null;
		}
		return <div class="pad"><div class="broadcast-red pad">
			<h3>{room.connectError || TL`Error`}</h3>
			<p class="buttonbar"><button class="button" data-cmd="/close"><strong>{TL`[Close]`}</strong></button></p>
		</div></div>;
	}

	override render() {
		const room = this.props.room;
		const userListWidth = room.width < 550 ? 0 : 146;
		const challengeOpen = room.challengeMenuOpen || room.challenging || room.challenged;
		return <PSPanelWrapper room={room} focusClick noScroll fullSize>
			<ChatLog
				class={`chat-log${!userListWidth ? ' userlist-minimized' : ' hasuserlist'}${challengeOpen ? ' challenge-open' : ''}`}
				room={this.props.room} left={userListWidth} top={room.tour?.info.isActive ? 30 : 0}
			>
				{this.renderControls()}
			</ChatLog>
			{room.tour && <TournamentBox tour={room.tour} left={userListWidth} />}
			<ChatTextEntry
				room={this.props.room} onMessage={this.send} onKey={this.onKey} left={userListWidth}
				tinyLayout={room.width - userListWidth < 400}
			/>
			<ChatUserList room={this.props.room} minimized={!userListWidth} />
		</PSPanelWrapper>;
	}
}

export class ChatUserList extends preact.Component<{
	room: ChatRoom, left?: number, top?: number, minimized?: boolean, static?: boolean,
}> {
	override render() {
		const room = this.props.room;
		const pmTargetid = room.pmTarget ? toID(room.pmTarget) : null;
		const userCountText = room.userCount === 1 ? TL`${room.userCount} user` : TL`${room.userCount} users`;
		return <div
			class={'userlist' + (this.props.minimized ? ' userlist-hidden' : this.props.static ? ' userlist-static' : '')}
			style={{ left: this.props.left || 0, top: this.props.top || 0 }}
		>
			{!this.props.minimized ? (
				<div class="userlist-count"><small>{userCountText}</small></div>
			) : room.id === 'dm-' ? (
				<>
					<button class="button button-middle" data-cmd="/help">{TL`[Commands]`}</button>
				</>
			) : pmTargetid ? (
				<>
					<button class="button button-middle" data-cmd="/challenge">{TL`[Challenge]`}</button>
					<button class="button button-middle" data-href={`useroptions-${pmTargetid}`}>{'\u2026'}</button>
				</>
			) : room.battle ? (
				<>
					<button data-href="userlist" class="button button-middle">{userCountText}</button>
					<button data-href="battleoptions" class="button button-middle">{TL`[Battle options]`}</button>
				</>
			) : (
				<button data-href="userlist" class="button button-middle">{userCountText}</button>
			)}
			<ul>
				{room.onlineUsers.map(([userid, name]) => {
					const groupSymbol = name.charAt(0);
					const group = PS.server.groups[groupSymbol] || { type: 'user', order: 0 };
					let color;
					if (name.endsWith('@!')) {
						name = name.slice(0, -2);
						color = '#888888';
					} else {
						color = BattleLog.usernameColor(userid);
					}
					return <li key={userid}><button class="userbutton username">
						<em class={`group${['leadership', 'staff'].includes(group.type!) ? ' staffgroup' : ''}`}>
							{groupSymbol}
						</em>
						{group.type === 'leadership' ? (
							<strong><em style={`color:${color}`}>{name.slice(1)}</em></strong>
						) : group.type === 'staff' ? (
							<strong style={`color:${color} `}>{name.slice(1)}</strong>
						) : (
							<span style={`color:${color}`}>{name.slice(1)}</span>
						)}
					</button></li>;
				})}
			</ul>
		</div>;
	}
}

class ChatLogInner extends preact.Component<{ class: string }> {
	override shouldComponentUpdate() {
		return false;
	}
	override render() {
		return <div><div class={this.props.class}></div></div>;
	}
}

export class ChatLog extends preact.Component<{
	class: string, room: ChatRoom, children?: preact.ComponentChildren,
	left?: number, top?: number, bottom?: number, noSubscription?: boolean, hasPreempt?: boolean,
}> {
	subscription: PSSubscription | null = null;
	moveLogContents(source: HTMLDivElement, target: HTMLDivElement) {
		if (source === target) return;
		const parent = target.parentElement!;
		parent.removeChild(target);
		parent.appendChild(source);
	}
	override componentDidMount() {
		const room = this.props.room;
		const elem = this.base as HTMLDivElement;
		let innerElem = elem.querySelector<HTMLDivElement>('.inner')!;
		let preemptElem = elem.querySelector<HTMLDivElement>('.inner-preempt');
		if (room.log) {
			this.moveLogContents(room.log.innerElem, innerElem);
			innerElem = room.log.innerElem;
			if (room.log.preemptElem && preemptElem) {
				this.moveLogContents(room.log.preemptElem, preemptElem);
				// preemptElem = room.log.preemptElem;
			}
			room.log.elem = elem;
			room.log.className = elem.className;
			elem.onscroll = room.log.onScroll;
			elem.onclick = room.log.onClick;
		}
		if (!this.props.noSubscription) {
			room.log ||= new BattleLog(elem, null, innerElem);
			room.log.getHighlight = room.handleHighlight;
			room.log.isIgnored = room.isIgnored;
			room.log.canRevealMessages = () => {
				const group = PS.server.getGroup(room.users[PS.user.userid]);
				return group.type === 'staff' || group.type === 'leadership';
			};
			if (room.backlog) {
				const backlog = room.backlog;
				room.backlog = null;
				for (const args of backlog) {
					room.log.add(args, undefined, undefined, PS.prefs.timestamps[room.pmTarget ? 'pms' : 'chatrooms']);
				}
			}
			this.subscription = room.subscribe(tokens => {
				if (!tokens) return;
				this.props.room.log!.add(tokens, undefined, undefined, PS.prefs.timestamps[room.pmTarget ? 'pms' : 'chatrooms']);
			});
		}
	}
	override componentWillUnmount() {
		this.subscription?.unsubscribe();
	}
	override componentDidUpdate() {
		this.updateScroll();
	}
	updateScroll() {
		this.props.room.log?.updateScroll();
	}
	override render() {
		return <div
			class={this.props.class} role="log" aria-label={TL`Chat log`}
			style={{ left: this.props.left || 0, top: this.props.top || 0, bottom: this.props.bottom ?? 40 }}
		>
			<ChatLogInner class="inner message-log" />
			{this.props.hasPreempt && <ChatLogInner class="inner-preempt message-log" />}
			{this.props.children && <div class="controls">{this.props.children}</div>}
		</div>;
	}
}

export class PSTextarea extends preact.Component<{
	defaultValue?: string, name?: string, placeholder?: string, class?: string,
	onInput?: (e: Event) => void, onKeyDown?: (e: KeyboardEvent) => void,
	minHeight?: string, minWidth?: string,
	/**
	 * A single-line textarea that still wraps: strips linebreaks, submits its
	 * containing form on Enter, and grows both horizontally and vertically.
	 */
	singleLine?: boolean,
	/** looks like plain text until hovered, focused, or changed (see `.textbox.textbox-subtle`) */
	subtle?: boolean,
}> {
	cssAutosize = !!window.CSS?.supports?.('field-sizing', 'content');
	updateSize = () => {
		const textbox = this.base!.querySelector('textarea')!;
		textbox.setAttribute('data-changed', textbox.value === (this.props.defaultValue || '') ? '' : '1');
		if (this.cssAutosize) return;

		const textboxTest = this.base!.querySelector<HTMLTextAreaElement>('textarea.heighttester')!;
		textboxTest.style.width = `${textbox.offsetWidth}px`;
		textboxTest.value = textbox.value;
		// +2 for the borders
		textbox.style.height = `${textboxTest.scrollHeight + 2}px`;
	};
	handleInput = (e: Event) => {
		if (this.props.singleLine) {
			const textbox = e.currentTarget as HTMLTextAreaElement;
			if (/[\r\n]/.test(textbox.value)) textbox.value = textbox.value.replace(/[\r\n]+/g, '');
		}
		this.updateSize();
		this.props.onInput?.(e);
	};
	handleKeyDown = (e: KeyboardEvent) => {
		if (this.props.singleLine && e.keyCode === 13 && !e.shiftKey) { // Enter
			e.preventDefault();
			(e.currentTarget as HTMLElement).closest('form')?.requestSubmit();
		}
		this.props.onKeyDown?.(e);
	};
	override componentDidMount(): void {
		this.updateSize();
		window.addEventListener('resize', this.updateSize);
	}
	override componentWillUnmount(): void {
		window.removeEventListener('resize', this.updateSize);
	}
	override render() {
		const className = [
			'textbox', this.props.subtle && 'textbox-subtle',
			this.props.singleLine && 'textbox-singleline', this.props.class,
		].filter(Boolean).join(' ');
		let style = `min-height:${this.props.minHeight || (this.props.singleLine ? '1em' : '3em')}`;
		const minWidth = this.props.minWidth || (this.props.singleLine ? '5em' : '');
		if (minWidth) style += `;min-width:${minWidth}`;
		const wrapperStyle = this.props.singleLine ?
			'position:relative;display:inline-block;max-width:100%;vertical-align:middle;margin:-4px' : 'position:relative';
		return <div style={wrapperStyle}>
			<textarea
				name={this.props.name} class={className} style={style} placeholder={this.props.placeholder}
				defaultValue={this.props.defaultValue}
				onInput={this.handleInput} onKeyUp={this.updateSize} onKeyDown={this.handleKeyDown}
			/>
			{!this.cssAutosize && <div><textarea
				class={`${className} heighttester`}
				style="visibility:hidden;position:absolute;left:-200px;height:10px;overflow-y:hidden"
			/></div>}
		</div>;
	}
}

PS.addRoomType(ChatPanel);
